import { useState } from "react";
import {
  Bell,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CircleCheckBig,
  CircleDot,
  Clock,
  LayoutGrid,
  Timer,
  X,
} from "lucide-react";
import {
  alternateTimeFormat,
  formatMinutesForDisplay,
  formatTimeForDisplay,
  useDurationDisplayFormat,
  useTimeDisplayFormat,
  type TimeDisplayFormat,
} from "@/lib/displayFormat";
import { parseDateKey, timeStringToMinutes } from "@/lib/time";
import type { TaskTimingValue } from "@/lib/taskTiming";
import type { TaskLifecycle } from "@shared/taskLifecycle";

export type TaskTimingHeaderProps = {
  kind: "schedule" | "status" | "pattern";
  timing: TaskTimingValue;
  label?: string;
  lifecycle?: TaskLifecycle;
  emptyLabel?: string;
  alertEnabled?: boolean;
  open?: boolean;
  expandable?: boolean;
  onClick: () => void;
  color?: string;
};

export default function TaskTimingHeader({
  kind,
  timing,
  label,
  lifecycle,
  emptyLabel = "Schedule",
  alertEnabled = false,
  open = false,
  expandable = true,
  onClick,
  color,
}: TaskTimingHeaderProps) {
  const [showAlternate, setShowAlternate] = useState(false);
  const { format } = useTimeDisplayFormat();
  const { format: durationFormat } = useDurationDisplayFormat();
  const startMinutes = timing.startTime
    ? timeStringToMinutes(timing.startTime)
    : null;
  const endMinutes = timing.endTime
    ? timeStringToMinutes(timing.endTime)
    : null;
  const parsedDuration = Number.parseInt(timing.durationMinutes, 10);
  const durationMinutes =
    Number.isFinite(parsedDuration) && parsedDuration > 0
      ? parsedDuration
      : null;
  const hasAlternate =
    startMinutes != null || endMinutes != null || durationMinutes != null;
  const showDate = kind === "schedule" && Boolean(timing.startDate);
  const showLeading =
    kind === "pattern" ||
    kind === "schedule" ||
    (kind === "status" && Boolean(lifecycle));

  return (
    <div
      className={`timing-header ${showAlternate && hasAlternate ? "show-alternate" : ""}`.trim()}
      style={{ color }}
    >
      {showLeading && (
        <button
          type="button"
          className="alt-button"
          aria-label={
            showAlternate ? "Show default format" : "Show alternate format"
          }
          aria-pressed={showAlternate}
          disabled={!hasAlternate}
          onClick={() => setShowAlternate(current => !current)}
        >
          <span className="leading">
            {kind === "pattern" ? (
              <LayoutGrid />
            ) : kind === "schedule" ? (
              <CalendarDays />
            ) : lifecycle ? (
              <StatusIcon lifecycle={lifecycle} />
            ) : null}
          </span>
        </button>
      )}
      <button type="button" className="header-button" onClick={onClick}>
        {label && <span className="label">{label}</span>}
        <span className="format">
          <span className="default-format">
            {showDate && (
              <span className="summary-text">
                {timing.startDate
                  ? formatTaskDateShort(timing.startDate)
                  : emptyLabel}
              </span>
            )}
            <TimingSummary
              timing={timing}
              timeFormat={format}
              durationFormat={durationFormat}
            />
          </span>
          {hasAlternate && (
            <span className="alternate-format">
              {showDate && (
                <span className="summary-text">
                  {timing.startDate
                    ? formatTaskDateShort(timing.startDate)
                    : emptyLabel}
                </span>
              )}
              <TimingSummary
                timing={timing}
                timeFormat={alternateTimeFormat(format)}
                durationFormat={
                  durationFormat === "compact" ? "alternate" : "compact"
                }
              />
            </span>
          )}
        </span>
        {kind !== "status" && alertEnabled && (
          <Bell className="alert-icon" style={{ opacity: 0.55 }} />
        )}
        {expandable &&
          (open ? (
            <ChevronDown className="chevron open" />
          ) : (
            <ChevronRight className="chevron" />
          ))}
      </button>
    </div>
  );
}

function TimingSummary({
  timing,
  timeFormat,
  durationFormat,
}: {
  timing: TaskTimingValue;
  timeFormat: TimeDisplayFormat;
  durationFormat: TimeDisplayFormat;
}) {
  const startMinutes = timing.startTime
    ? timeStringToMinutes(timing.startTime)
    : null;
  const endMinutes = timing.endTime
    ? timeStringToMinutes(timing.endTime)
    : null;
  const parsedDuration = Number.parseInt(timing.durationMinutes, 10);
  const durationMinutes =
    Number.isFinite(parsedDuration) && parsedDuration > 0
      ? parsedDuration
      : null;
  if (startMinutes == null && endMinutes == null && durationMinutes == null)
    return null;

  return (
    <span className="time-content">
      {startMinutes != null && (
        <span>{formatTimeForDisplay(startMinutes, timeFormat)}</span>
      )}
      {startMinutes != null && endMinutes != null && (
        <span className="time-arrow">-</span>
      )}
      {endMinutes != null && (
        <span>{formatTimeForDisplay(endMinutes, timeFormat)}</span>
      )}
      {durationMinutes != null && (
        <span className="duration">
          <Timer className="duration-icon" />
          <span>
            {formatMinutesForDisplay(durationMinutes, durationFormat)}
          </span>
        </span>
      )}
    </span>
  );
}

function StatusIcon({ lifecycle }: { lifecycle: TaskLifecycle }) {
  if (lifecycle === "doing") return <CircleDot className="status-icon" />;
  if (lifecycle === "done") return <CircleCheckBig className="status-icon" />;
  if (lifecycle === "skipped") return <X className="status-icon" />;
  return <Clock className="status-icon" />;
}

function formatTaskDateShort(value: string) {
  try {
    return parseDateKey(value).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return value;
  }
}
